/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Component, ComponentRef, OnInit} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';

import {TranslateService} from '@ngx-translate/core';

import {merge, Observable} from 'rxjs';
import {map, takeUntil, tap} from 'rxjs/operators';

import {ESSService} from '../+ess/employee-self.service';
import {GP_DATE_FORMAT, GP_TIMEZONE_LIST, NUMBER_DECIMAL_SYMBOLS, NUMBER_SEPARATOR_SYMBOLS} from '../app.constants';
import {Destroyable} from '../destroyable.component';
import {ApiService} from '../shared/api/api.service';
import {Employee} from '../shared/employees/employees.model';
import {I18nManager} from '../shared/i18n/i18n.manager';
import {LocalizationService} from '../shared/localization/localization.service';
import {ModalCommand, ModalState} from '../shared/modal/modal.component';
import {ModalService} from '../shared/modal/modal.service';
import {User, UserService} from '../shared/user';
import {UserAvatarCropperComponent} from '../shared/user/user-avatar-cropper.component';
import {getDefaultLocalTimezone} from '../shared/utils/date';
import {isPresent} from '../shared/utils/lang';


@Component({
  selector: 'gp-modal-my-profile',
  templateUrl: './my-profile.component.html',
  styleUrls: ['./my-profile.component.css']
})
export class MyProfileComponent extends Destroyable implements OnInit {

  public user: User;
  public cancelCallback: () => Promise<any>;
  public submitCallback: () => Observable<ModalCommand>;
  public employee: Employee;

  public currentSendEmail: boolean;
  public newSendEmail: boolean;
  public currentEmail: string;
  public newEmail: string;
  public mailControl: UntypedFormControl = new UntypedFormControl();

  public languages: { [key: string]: string } = {};
  public currentLanguage: string;

  public currentTimezone: string = '';
  public timezoneNames: any;
  public currentNumberFormat: string;
  public numberDecimalSymbols;
  public numberSeparatorSymbols;
  public currentDateFormat: string;
  public dateFormats;
  public currentAutoSave;
  public selectedLocale: any;
  private selectedTimezone: string;
  private selectedDateFormat: string;
  private selectedNumberFormat;
  private selectedAutoSave;

  constructor(private userService: UserService,
              private i18nManager: I18nManager,
              private localization: LocalizationService,
              private modalService: ModalService,
              private essService: ESSService,
              private translate: TranslateService,
              public api: ApiService) {
    super();
    this.languages = this.i18nManager.availableLanguages;
    this.currentLanguage = this.i18nManager.currentLanguage;
    this.numberSeparatorSymbols = NUMBER_SEPARATOR_SYMBOLS;
    this.numberDecimalSymbols = NUMBER_DECIMAL_SYMBOLS;
    this.dateFormats = GP_DATE_FORMAT;
  }

  public ngOnInit() {
    merge(
      this.userService.getUser().pipe(map((user: User) => this.user = user)),
      this.translate.onLangChange
    )
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        if (this.user) {
          this.currentTimezone = this.user.timezone || getDefaultLocalTimezone();
          this.timezoneNames = GP_TIMEZONE_LIST;
          this.currentDateFormat = this.localization.dateFormat;
          this.currentAutoSave = !!this.user.autoSave;
          this.currentEmail = this.user.email;
          this.mailControl.setValue(this.currentEmail);
          this.currentSendEmail = this.user.sendEmail;
        }
      });

    this.essService.currentUser
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        this.employee = this.essService.employee;
      });

    this.mailControl.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((value: string) => {
        if (this.currentEmail !== value) {
          this.currentSendEmail = false;
          this.newSendEmail = false;
        }
        this.newEmail = value;
      });

    this.currentNumberFormat = this.localization.numberFormat.decimal + this.localization.numberFormat.separator;
    this.cancelCallback = this.onCancel.bind(this);
    this.submitCallback = this.onSubmit.bind(this);
  }

  public selectTimezone(zone: string): void {
    this.selectedTimezone = zone;
  }

  public selectNumberFormat(format: string): void {
    this.selectedNumberFormat = {
      separator: format[1],
      decimal: format[0]
    };
  }

  public selectDateFormat(format: string): void {
    this.selectedDateFormat = format;
  }

  public selectAutoSave(value): void {
    this.selectedAutoSave = value;
  }

  public photoSelected(event: any) {
    if (event.target.files.length > 0) {
      this.modalService.openModalWithName('UserAvatarCropper')
        .then((modelRef: ComponentRef<UserAvatarCropperComponent>) => {
          modelRef.instance.employee = this.essService.employee;
          modelRef.instance.photoForUpload = event.target.files[0];
        });
    }
  }

  private onCancel(): Promise<any> {
    return Promise.resolve();
  }

  private updateSettings() {
    const data: {
      id: number,
      timezone?: string,
      locale?: string,
      dateFormat?: string,
      integerMask?: string,
      decimalMask?: string,
      autoSave?: boolean,
      sendEmail?: boolean,
      email?: string
    } = {
      id: this.user.id
    };

    let updateUserDataObs: Observable<any> = this.userService.updateUserData(data);

    if (this.selectedDateFormat) {
      data.dateFormat = this.selectedDateFormat;
      updateUserDataObs = updateUserDataObs.pipe(
        tap(() => this.user.dateFormat = this.selectedDateFormat)
      );
    }

    if (this.selectedNumberFormat) {
      data.integerMask = this.selectedNumberFormat.separator;
      data.decimalMask = this.selectedNumberFormat.decimal;
      updateUserDataObs = updateUserDataObs.pipe(
        tap(() => {
          this.user.integerMask = this.selectedNumberFormat.separator;
          this.user.decimalMask = this.selectedNumberFormat.decimal;
        })
      );
    }

    if (this.selectedTimezone) {
      data.timezone = this.selectedTimezone;
      updateUserDataObs = updateUserDataObs.pipe(
        tap(() => this.user.timezone = this.selectedTimezone)
      );
    }

    if (this.selectedLocale) {
      data.locale = this.selectedLocale;
      updateUserDataObs = updateUserDataObs.pipe(
        tap(() => this.user.language = this.selectedLocale)
      );
    }

    if (this.newEmail && this.mailControl.valid && this.newEmail !== this.currentEmail) {
      data.email = this.newEmail;
      updateUserDataObs = updateUserDataObs.pipe(
        tap(() => this.user.email = this.newEmail)
      );
    }

    if (isPresent(this.newSendEmail)) {
      data.sendEmail = this.newSendEmail;
      updateUserDataObs = updateUserDataObs.pipe(
        tap(() => this.user.sendEmail = this.newSendEmail)
      );
    }

    if (isPresent(this.selectedAutoSave)) {
      data.autoSave = this.selectedAutoSave;
      updateUserDataObs = updateUserDataObs.pipe(
        tap(() => this.user.autoSave = this.selectedAutoSave)
      );
    }

    return updateUserDataObs.pipe(
      tap(() => this.userService.userSubject.next(this.user))
    );
  }

  private onSubmit(): Observable<ModalCommand> {
    return this.updateSettings()
      .pipe(
        map(() => ({
          state: ModalState.VALID,
          message: 'OK',
          duration: 1000
        }))
      );
  }

  public onChange(target: EventTarget): void {
    this.newSendEmail = (<HTMLInputElement>target).checked;
  }
}
